/**
 * V1.5 Theme-Provider.
 * Steuert data-theme + data-density auf <html>. Persistiert über localStorage
 * (FOUC-Schutz vor Login). Server-Sync via PATCH /v1/users/me/preferences ist
 * Phase 10 — hier nur Client-State.
 *
 * Quelle: Implementation Brief §5.6, §7.9.
 */
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'mindsquare' | 'mission-control';
export type Density = 'calm' | 'balanced' | 'dense';

const THEME_STORAGE_KEY = 'pv:theme';
const DENSITY_STORAGE_KEY = 'pv:density';

const DEFAULT_THEME: Theme = 'mindsquare';
const DEFAULT_DENSITY: Density = 'dense';

type ThemeContextValue = {
  theme: Theme;
  density: Density;
  setTheme: (t: Theme) => void;
  setDensity: (d: Density) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return stored === 'mission-control' ? 'mission-control' : DEFAULT_THEME;
}

function readStoredDensity(): Density {
  if (typeof window === 'undefined') return DEFAULT_DENSITY;
  const stored = window.localStorage.getItem(DENSITY_STORAGE_KEY);
  if (stored === 'calm' || stored === 'dense') return stored;
  return DEFAULT_DENSITY;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [density, setDensityState] = useState<Density>(readStoredDensity);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    if (density === 'balanced') {
      root.removeAttribute('data-density');
    } else {
      root.setAttribute('data-density', density);
    }
    window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
  }, [density]);

  return (
    <ThemeContext.Provider
      value={{ theme, density, setTheme: setThemeState, setDensity: setDensityState }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

/**
 * FOUC-Schutz: vor React-Hydration setzt dieses Snippet bereits data-theme,
 * damit kein Flash zwischen Default und User-Preferenz auftritt. Aufrufen via
 * inline-Script in index.html. Pure JS, keine TS-Abhängigkeiten.
 */
export const themeBootstrapScript = `(function() {
  try {
    var t = localStorage.getItem('${THEME_STORAGE_KEY}');
    document.documentElement.setAttribute('data-theme', t === 'mission-control' ? 'mission-control' : '${DEFAULT_THEME}');
    var d = localStorage.getItem('${DENSITY_STORAGE_KEY}');
    if (d === 'calm' || d === 'dense') document.documentElement.setAttribute('data-density', d);
  } catch (e) {}
})();`;
